import { defineNuxtPlugin, useRouter } from "#app";

import { getEnv } from "@solvari/utils";

import {
  usePageUris,
  useProductOverviewSlugs,
  useProvinceSlugs,
} from "~/app/api/routing.api.ts";
import {
  buildConstraint,
  buildCustomClientRoutes,
  buildStatamicRoutes,
} from "~/app/routes.helpers.ts";
import { useQueryAsync } from "~/shared/libs/useQueryAsync.ts";

export default defineNuxtPlugin(async () => {
  const locale = getEnv().config.localeCode;

  const results = await Promise.all([
    useQueryAsync(useProductOverviewSlugs({ locale })),
    useQueryAsync(usePageUris({ blueprint: "staticPage", locale })),
    useQueryAsync(usePageUris({ blueprint: "performance", locale })),
    useQueryAsync(useProvinceSlugs({ locale })),
  ]);
  const [
    constrainedResult,
    staticPageResult,
    performanceResult,
    provinceResult,
  ] = results;

  if (
    constrainedResult instanceof Error ||
    staticPageResult instanceof Error ||
    performanceResult instanceof Error ||
    provinceResult instanceof Error
  ) {
    const cause = results.find((result) => result instanceof Error);
    throw new Error("Failed to load route constraints", { cause });
  }

  // Both sides populate Pinia Colada. Server repeats router.options' fetches (proxy absorbs them).
  // Routes added on client only — the server has them via router.options.
  if (import.meta.server) {
    return;
  }

  const overviewConstraint = buildConstraint(constrainedResult);
  const provinceConstraint = buildConstraint(provinceResult);
  const staticPageConstraint = buildConstraint(staticPageResult);
  const performanceConstraint = buildConstraint(performanceResult);

  const router = useRouter();

  [
    ...buildCustomClientRoutes(provinceConstraint),
    ...buildStatamicRoutes(
      {
        overview: overviewConstraint,
        performance: performanceConstraint,
        staticPage: staticPageConstraint,
      },
      locale,
    ),
  ].forEach(router.addRoute);
});
